import React, {useCallback, useEffect, useImperativeHandle, useRef} from 'react';
import styled from 'styled-components';
import {Actions, CameraAction, FlipAction, HangupAction, MuteAction, SpeakerAction} from "./actions";
import {FullUserProfile, FullUserVideo, InviteButton, UserCard, UserCards} from "./users";
import {TUICallCameraPosition, TUICallEvent, TUICallType} from "tuicall-engine-webrtc";
import {joinCall, startCall} from "./tencent";
import produce, {current} from "immer";
import {getCallUsers, sleep, T} from "../Chat/connector";
import TIM from "tim-js-sdk";
import EVENT = TIM.EVENT;

export interface ICallRef {
    onStartCall: (params: {
        callType: TUICallType, userIds: Array<any>, groupID?: string
    }) => void,
    onAcceptInviteCall: (params: any) => void,
    endCall: () => void,
}

const Call = React.forwardRef((props: {
    callEngine: any,
    user: any,
    mode?: "admin" | "customer",
    onInvite?: (data: any) => void,
    onChange?: (calling: boolean, callInfo: any) => void,
    view?: 'history' | 'chat' | 'call',
    onStartCall?: (callInfo: any) => void,
    onEndCall?: (callInfo: any) => void,
    onCreateInvite?: () => void,
    onError?: (message: string) => void,
    onUserLeave?: (userID: string, users: Array<any>) => void,
}, ref: React.ForwardedRef<ICallRef>) => {
    const {callEngine, user} = props;
    const mainCameraIdRef = useRef('main-camera');
    const [users, setUsers] = React.useState([]);
    const [callInfo, setCallInfo] = React.useState<{
        roomID?: string,
        callStatus?: string,
        isHost?: boolean,
        callType?: TUICallType,
        calling?: boolean,
        microphoneList?: Array<any>,
        cameraList?: Array<any>,
        speakerList?: Array<any>,
        currentMicrophone?: string,
        currentCamera?: string,

        speakerOff?: boolean,
        microphoneOff?: boolean,
        cameraOff?: boolean,
        isFrontCamera?: boolean
    }>({});
    const usersRef = useRef<Array<any>>([]);
    const cachedDataRef = useRef<any>({});
    const tencentGroupIdRef = useRef<string>("");

    useImperativeHandle(ref, () => ({
        onStartCall: (params: {
            callType: TUICallType, userIds: Array<any>, groupID?: string
        }) => onStartCall(params),
        onAcceptInviteCall: (params: any) => onAcceptInviteCall(params),
        onJoinCall: (params: any) => onJoinCall(params),
        endCall: () => onEndCall(),
    }));

    useEffect(() => {
        props?.onChange(callInfo?.calling, callInfo);
    }, [callInfo?.calling]);

    useEffect(() => {
        // Add event listeners for call events
        if (!!callEngine) {
            console.log("Call engine initialized:", callEngine, user);
            callEngine.on(TUICallEvent.INVITED, (event) => {
                console.log("Call invited:", event);
                if (props?.onInvite) {
                    const userData = JSON.parse(event?.userData || "{}");
                    props.onInvite({
                        ...userData,
                        callType: userData.callType,
                    });
                } else {
                }
            })

            callEngine.on(TUICallEvent.DEVICED_UPDATED, (event) => {
                setCallInfo(produce((draft: any) => {
                    draft.microphoneList = event?.microphoneList || [];
                    draft.cameraList = event?.cameraList || [];
                    draft.currentMicrophone = event?.currentMicrophone || "";
                    draft.currentCamera = event?.currentCamera || "";
                }))
            });

            callEngine.on(TUICallEvent.USER_AUDIO_AVAILABLE, (event) => {
                console.log("User audio available:", event);
                setUsers(produce(draft => {
                    const existingUserIndex = draft.findIndex(item => item.userID === event?.userID);
                    if (existingUserIndex >= 0) {
                        draft[existingUserIndex].isAudioAvailable = event?.isAudioAvailable || false;
                    } else {
                        // If user is not in the list, add them
                        draft.push({
                            userID: event?.userID,
                            name: "",
                            avatar: 'https://null.png',
                            main: false,
                            isAudioAvailable: event?.isAudioAvailable || false
                        });
                    }
                    usersRef.current = current(draft);
                }))
            });

            callEngine.on(TUICallEvent.USER_VIDEO_AVAILABLE, (event) => {
                console.log("User video available:", event);
                setUsers(produce(draft => {
                    const existingUserIndex = draft.findIndex(item => item.userID === event?.userID);
                    if (existingUserIndex >= 0) {
                        draft[existingUserIndex].isVideoAvailable = event?.isVideoAvailable || false;
                    } else {
                        // If user is not in the list, add them
                        draft.push({
                            userID: event?.userID,
                            name: "",
                            avatar: 'https://null.png',
                            main: false,
                            isVideoAvailable: event?.isVideoAvailable || false
                        });
                    }
                    // if video is available, change type to video call
                    if (event?.isVideoAvailable) {
                        setCallInfo(produce((draft) => {
                            draft.callType = TUICallType.VIDEO_CALL;
                        }));
                    }
                    usersRef.current = current(draft);
                }))
            });

            callEngine.on(TUICallEvent.ON_CALL_BEGIN, (event) => {
                console.log("Call ON_CALL_BEGIN:", event, callEngine.getCallStatus?.());
                setCallInfo(produce((draft: any) => {
                    draft.callStatus = callEngine.getCallStatus?.() || "";
                }))
            });

            callEngine.on(TUICallEvent.ON_CALL_RECEIVED, (event) => {
                console.log("Call ON_CALL_RECEIVED:", event, callEngine.getCallStatus?.());
                setCallInfo(produce((draft: any) => {
                    draft.callStatus = callEngine.getCallStatus?.() || "";
                }))
            });

            callEngine.on(TUICallEvent.CALL_END, (event) => {
                console.log("Call ended:", event);
                setCallInfo(produce((draft) => {
                    draft.calling = false;
                    props?.onEndCall?.({
                        ...current(draft),
                        users: usersRef.current,
                    });
                }));
                sleep(500).then(() => {
                    setUsers([]);
                    usersRef.current = [];
                });
            })
            callEngine.on(TUICallEvent.CALLING_CANCEL, (event) => {
                console.log("Call cancelled:", event);
                props?.onChange(null, callInfo)
                setCallInfo(produce((draft) => {
                    draft.calling = null;
                    props?.onEndCall?.({
                        ...current(draft),
                        users: usersRef.current,
                    });
                }));
                sleep(500).then(() => {
                    setUsers([]);
                    usersRef.current = [];
                });
            });

            callEngine.on(TUICallEvent.CALLING_TIMEOUT, (event) => {
                console.log("Call timeout:", event);
                setCallInfo(produce((draft) => {
                    draft.calling = null;
                    props?.onEndCall?.({
                        ...current(draft),
                        users: usersRef.current,
                    });
                }));
                sleep(500).then(() => {
                    setUsers([]);
                    usersRef.current = [];
                });
            });
            callEngine.on(TUICallEvent.USER_ENTER, async (event) => {
                console.log("Call joined:", event);
                if (event?.userID?.includes("_transcription")) {
                    // Skip transcription users
                    console.log("Skipping transcription user:", event?.userID);
                    return;
                }
                addUser(event?.userID).finally(() => {
                    addUserFromDB(event?.userID).finally(() => {
                        sleep(500).finally(() => {
                            onUpdateListUserProfile().then();
                        });
                    });
                });
            });
            callEngine.on(TUICallEvent.USER_LEAVE, (event) => {
                console.log("User left the call:", event);
                props?.onUserLeave?.(event?.userID, usersRef.current);
                // Check if user left call and only me in call => end call
                const newUsers = usersRef.current.filter(item => item.userID !== event?.userID);
                // If only me left in the call, end the call
                if (newUsers.length === 1 && newUsers[0].userID === user?.userName) {
                    console.log("Only me left, ending call...");
                    onEndCall();
                    return;
                }
                setUsers(produce(draft => {
                    // Update the main user if the current main user left
                    if (draft.length > 0 && !!draft.find(item => item.userID === event?.userID)?.main) {
                        let nextMainUserIndex = draft.filter(item => item?.main && item?.userID !== event?.userID).findIndex(item => item.userID !== event?.userID);
                        if (nextMainUserIndex === -1) {
                            nextMainUserIndex = 0; // If no other main user, set the first user as main
                        }
                        draft.forEach(item => {
                            item.main = false; // Reset all users to not main
                        });
                        draft[nextMainUserIndex].main = true; // Set the next user as main
                    }

                    const index = draft.findIndex(item => item.userID === event?.userID);
                    if (index !== -1) {
                        draft.splice(index, 1);
                    }
                    usersRef.current = current(draft);
                }));
            });

            callEngine.tim.on(EVENT.FRIEND_GROUP_LIST_UPDATED, (event) => {
                console.log("Group FRIEND_GROUP_LIST_UPDATED:", event);
            });
            callEngine.tim.on(TIM.EVENT.PROFILE_UPDATED, function (event) {
                console.log('Profile updated:', event.data);
                // event.data chứa array các Profile objects đã được update
            });
        }

        return () => {
            // Clean up event listeners when the component unmounts
            if (callEngine) {
                callEngine.off(TUICallEvent.INVITED);
                callEngine.off(TUICallEvent.USER_VIDEO_AVAILABLE);
                callEngine.off(TUICallEvent.ON_CALL_BEGIN);
                callEngine.off(TUICallEvent.ON_CALL_RECEIVED);
                callEngine.off(TUICallEvent.USER_ACCEPT);
                callEngine.off(TUICallEvent.CALL_END);
                callEngine.off(TUICallEvent.CALLING_CANCEL);
                callEngine.off(TUICallEvent.CALLING_TIMEOUT);
                callEngine.off(TUICallEvent.USER_ENTER);
                callEngine.off(TUICallEvent.USER_AUDIO_AVAILABLE);
                callEngine.off(TUICallEvent.DEVICED_UPDATED);
                callEngine.off(TUICallEvent.USER_LEAVE);
            }
        }
    }, [!!callEngine]);

    useEffect(() => {
        // Set the main camera ID for the call
        console.log("Users in call:", users, callInfo);

        if (users?.length && callInfo.calling && callInfo?.callType === TUICallType.VIDEO_CALL && (callInfo?.callStatus === "connected" || (callInfo?.callStatus === "calling" && callInfo.isHost))) {
            // Set the main camera ID
            const mainUser = users.find(user => user.main);
            const callStatus = callEngine?.getCallStatus?.();
            if (mainUser) {
                if (mainUser.userID === user?.userName) {
                    callEngine.openCamera(mainCameraIdRef.current).catch(reason => null);
                } else {
                    if (callStatus === "connected") {
                        callEngine.startRemoteView({
                            userID: mainUser.userID,
                            videoViewDomID: mainCameraIdRef.current
                        }).catch(reason => null);
                    }
                }
            }
            // Set the users for the call
            console.log("Setting cameras for users:", users);
            users.filter(item => !item?.main && item?.isVideoAvailable).map(item => {
                const cameraId = `camera-${item.userID}`;
                console.log("Setting camera for user:", item.userID, cameraId);
                if (item.userID === user?.userName) {
                    callEngine.openCamera(cameraId).catch(reason => null);
                } else {
                    if (callStatus === "connected") {
                        console.log('Starting remote view for user:', item.userID, cameraId);
                        callEngine.startRemoteView({
                            userID: item.userID,
                            videoViewDomID: cameraId
                        }).catch(reason => null);
                    }
                }
            })
        }
    }, [users, callInfo?.callType, callInfo.calling, callInfo?.callStatus]);

    const updateDevices = async () => {
        const result = {
            microphoneList: [],
            cameraList: [],
            speakerList: [],
        }
        // Get All Devices
        result.microphoneList = await callEngine.getDeviceList("microphones").then((result) => {
            setCallInfo(produce((draft: any) => {
                draft.microphoneList = result || [];
            }));
            return result;
        }).catch((error) => {
            console.error("Error getting microphone list:", error);
            return [];
        });
        result.cameraList = await callEngine.getDeviceList("camera").then((result) => {
            setCallInfo(produce((draft: any) => {
                draft.cameraList = result || [];
            }));
            return result;
        }).catch((error) => {
            console.error("Error getting camera list:", error);
            return [];
        });
        result.speakerList = await callEngine.getDeviceList("speaker").then((result) => {
            console.log("Speaker list:", result);
            setCallInfo(produce((draft: any) => {
                draft.speakerList = result || [];
            }));
            return result;
        }).catch((error) => {
            console.error("Error getting speaker list:", error);
            return [];
        });
        return result;
    }

    const onUpdateListUserProfile = useCallback(async () => {
        // Update users list when a new user joins the call
        try {
            const result = await callEngine?.tim?.getGroupMemberList?.({
                groupID: tencentGroupIdRef.current,
                count: 100,
                offset: 0
            });
            const remoteUsers = result?.data?.memberList || [];
            setUsers(produce(draft => {
                const currentUsers = current(draft) || [];
                currentUsers.map((currentUser, index) => {
                    let remoteUser = remoteUsers.find(item => item.userID === currentUser?.userID);
                    const cachedData = cachedDataRef.current?.['usersDB']?.[tencentGroupIdRef.current] || {};
                    if (cachedData?.[currentUser?.userID]) {
                        console.log("Using cached data for user:", currentUser?.userID, remoteUser, cachedData?.[currentUser?.userID], currentUser);
                        remoteUser = remoteUser || {};
                        // Use cached data if available
                        remoteUser.nick = cachedData?.[currentUser?.userID]?.nick || cachedData?.[currentUser?.userID]?.nickName || remoteUser?.nick || "";
                        remoteUser.avatar = cachedData?.[currentUser?.userID]?.avatar || remoteUser?.avatar;
                    }
                    draft[index].name = remoteUser?.nick || currentUser?.name || "";
                    draft[index].avatar = remoteUser?.avatar || currentUser?.avatar || 'https://null.png';
                })
                usersRef.current = current(draft);
                console.log('New users: ', usersRef.current)
            }))
        } catch (e) {
        }
    }, [callEngine, callInfo]);

    const addUser = useCallback(async (userID: string) => {
        // Update users list when a new user joins the call
        const result = await callEngine.tim.getGroupMemberList({
            groupID: tencentGroupIdRef.current,
            count: 100,
            offset: 0
        });
        const remoteUsers = result?.data?.memberList;
        setUsers(produce(draft => {
            const currentUsers = current(draft) || [];
            const existingUserIndex = currentUsers.findIndex(item => item.userID === userID);
            let remoteUser = remoteUsers?.find(item => item.userID === userID);
            const cachedData = cachedDataRef.current?.['usersDB']?.[tencentGroupIdRef.current] || {};
            if (cachedData?.[userID]) {
                remoteUser = remoteUser || {};
                // Use cached data if available
                remoteUser.nick = remoteUser?.nick || cachedData?.[userID]?.nick || cachedData?.[userID]?.nickName || "";
                remoteUser.avatar = remoteUser?.avatar || cachedData?.[userID]?.avatar;
            }
            if (existingUserIndex < 0) {
                // Set the main camera ID for the call
                draft.forEach(item => {
                    item.main = false;
                });
                draft.push({
                    userID: userID,
                    name: remoteUser?.nick || "",
                    avatar: remoteUser?.avatar || 'https://null.png',
                    main: true,
                    isVideoAvailable: false,
                    isAudioAvailable: false,
                });
            } else {
                draft[existingUserIndex].name = remoteUser?.nick || draft[existingUserIndex].name || "";
                draft[existingUserIndex].avatar = remoteUser?.avatar || draft[existingUserIndex].avatar || 'https://null.png';
            }
            usersRef.current = current(draft);
        }));
        await sleep(500);
    }, [callEngine, tencentGroupIdRef.current, users, callInfo]);
    const addUserFromDB = useCallback(async (userID: string) => {
        // Update users list when a new user joins the call
        const remoteUsers = await getCallUsers(tencentGroupIdRef.current);
        cachedDataRef.current['usersDB'] = cachedDataRef.current['usersDB'] ? cachedDataRef.current['usersDB'] : {};
        cachedDataRef.current['usersDB'][tencentGroupIdRef.current] = remoteUsers;

        console.log("Remote users:", remoteUsers, userID);
        setUsers(produce(draft => {
            const currentUsers = current(draft) || [];
            const existingUserIndex = currentUsers.findIndex(item => item.userID === userID);
            const remoteUser = remoteUsers?.[userID];

            if (existingUserIndex >= 0 && remoteUser) {
                console.log("Updating existing user:", remoteUser, remoteUser?.nick || currentUsers?.[existingUserIndex]?.name || "");
                draft[existingUserIndex].name = remoteUser?.nick || remoteUser?.nickName || currentUsers?.[existingUserIndex]?.name || "";
                draft[existingUserIndex].avatar = remoteUser?.avatar || currentUsers?.[existingUserIndex]?.avatar || 'https://null.png';
            }
            usersRef.current = current(draft);
        }));
        await sleep(500);
    }, [tencentGroupIdRef.current, users])

    const onStartCall = useCallback(async (params: {
        callType: TUICallType, userIds: Array<any>, groupID?: string, roomID?: string
    }) => {
        const devices = await updateDevices();

        tencentGroupIdRef.current = params?.groupID || "";
        setCallInfo(produce((draft) => {
            draft.calling = true;
            draft.callStatus = callEngine.getCallStatus?.() || "";
            draft.callType = params?.callType;
            draft.isHost = true;
            draft.speakerOff = false;
            draft.microphoneOff = false;
            // Set the not available camera if call type is not VIDEO_CALL or no camera devices available
            draft.cameraOff = (params?.callType !== TUICallType.VIDEO_CALL) || !devices?.cameraList.length;
            draft.isFrontCamera = true;
        }));


        const callResult = await startCall(callEngine, {
            callType: TUICallType.VIDEO_CALL, // Default to VIDEO_CALL
            userIDList: params?.userIds || [],
            groupID: params?.groupID,
            roomID: params?.roomID || "",
            userData: {
                callType: params?.callType,
                groupID: params?.groupID,
            }
        });
        if (callResult) {
            setCallInfo(produce((draft) => {
                draft.callStatus = callEngine.getCallStatus?.() || "";
                draft.roomID = callResult?.roomID || "";
                props?.onStartCall?.({
                    isHost: true,
                    callType: params?.callType,
                    userIds: params?.userIds || [],
                    groupID: params?.groupID,
                    roomID: callResult?.roomID || "",
                    ...current(draft),
                })
            }));
            setUsers([{
                userID: user?.userName,
                name: user?.fullName,
                avatar: user?.avatar || '',
                main: true,
                isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
            }]);
            usersRef.current = [{
                userID: user?.userName,
                name: user?.fullName,
                avatar: user?.avatar || '',
                main: true,
                isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
            }];

        } else {
            setCallInfo(produce((draft) => {
                draft.calling = false;
            }));

            props.onError?.(T("Start call failed"));
            console.error("Start call failed");
        }
    }, [callInfo, callEngine, user, props?.onStartCall, users]);

    const onAcceptInviteCall = useCallback(async (params: any) => {
        // Accept an incoming call invitation
        tencentGroupIdRef.current = params?.groupID || "";
        const devices = await updateDevices();
        setCallInfo(produce((draft) => {
            draft.calling = true;
            draft.callType = params?.callType;
            draft.cameraOff = (params?.callType !== TUICallType.VIDEO_CALL) || !devices?.cameraList?.length;
            draft.isHost = false;
            draft.speakerOff = false;
            draft.microphoneOff = false;
            draft.callStatus = callEngine.getCallStatus?.() || "";
            draft.roomID = params?.roomID || "";
        }));
        console.log('Accepting call with params:', params)
        setUsers([{
            userID: user?.userName,
            name: user?.fullName,
            avatar: user?.avatar || 'https://null.png',
            main: true,
            isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
        }]);
        usersRef.current = [{
            userID: user?.userName,
            name: user?.fullName,
            avatar: user?.avatar || 'https://null.png',
            main: true,
            isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
        }];
        await callEngine.accept().then(() => {
            props?.onStartCall?.({
                ...params,
                callInfo,
            });
        }).catch((error) => {
            console.error("Error accepting call:", error);
            setCallInfo(produce((draft) => {
                draft.calling = false;
            }));
            props.onError?.(T("Error accepting call"));
        });
    }, [callEngine, user, props?.onStartCall, callInfo, users]);

    // Function to join an existing call
    const onJoinCall = useCallback(async (params: any) => {
        // Join an existing call
        tencentGroupIdRef.current = params?.groupID || "";
        const devices = await updateDevices();
        setCallInfo(produce((draft) => {
            draft.calling = true;
            draft.callType = params?.callType;
            draft.cameraOff = (params?.callType !== TUICallType.VIDEO_CALL) || !devices?.cameraList?.length;
            draft.isHost = params?.isHost;
            draft.speakerOff = false;
            draft.microphoneOff = false;
            draft.callStatus = callEngine.getCallStatus?.() || "";
            draft.roomID = params?.roomID || "";
            console.log("Current call info:", current(draft));
        }));
        console.log('Joining call with params:', params)
        setUsers([{
            userID: user?.userName,
            name: user?.fullName,
            avatar: user?.avatar || 'https://null.png',
            main: true,
            isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
        }]);
        usersRef.current = [{
            userID: user?.userName,
            name: user?.fullName,
            avatar: user?.avatar || 'https://null.png',
            main: true,
            isVideoAvailable: (params.callType === TUICallType.VIDEO_CALL) && !!devices?.cameraList?.length
        }];

        console.log("Joining call with status", callEngine.getCallStatus?.());

        const result = await joinCall(callEngine, params).then(() => {
            props?.onStartCall?.({
                ...params,
                callInfo,
            });
        }).catch((error) => {
            console.error("Error joining call:", error);
            setCallInfo(produce((draft) => {
                draft.calling = false;
            }));
            props.onError?.(T("Error joining call"));
        });
    }, [callEngine, user, props?.onStartCall, callInfo, users]);

    const onEndCall = () => {
        if (callEngine) {
            console.log("Ending call...");
            // End the call using the call engine
            const callStatus = callEngine?.getCallStatus?.();
            if (["calling", "connected"].includes(callStatus)) {
                callEngine.hangup();
                setCallInfo(produce((draft) => {
                    draft.calling = false;
                    props?.onEndCall?.({
                        ...current(draft),
                        users: usersRef.current,
                    });
                }));
            }
            setCallInfo(produce((draft) => {
                draft.calling = false;
            }));
        } else {
            console.error("Call engine is not initialized");
        }
    }

    const onChangeCamera = useCallback(async () => {
        if (callEngine) {
            setCallInfo(produce((draft) => {
                if (!draft.isFrontCamera) {
                    callEngine.switchCamera(TUICallCameraPosition.BACK)
                } else {
                    callEngine.switchCamera(TUICallCameraPosition.FRONT)
                }
                draft.isFrontCamera = !draft.isFrontCamera;
            }));
        } else {
            console.error("Call engine is not initialized");
        }
    }, [callEngine, callInfo]);

    const onToggleMicrophone = useCallback(() => {
        if (callEngine) {
            console.log("Switching microphone...");
            // On off the microphone
            if (!!callInfo.microphoneOff) {
                callEngine.openMicrophone();
                setUsers(produce(draft => {
                    const currentUsers = current(draft) || [];
                    const existingUserIndex = currentUsers.findIndex(item => item.userID === user?.userName);
                    if (existingUserIndex >= 0) {
                        draft[existingUserIndex].isAudioAvailable = true;
                    }
                    usersRef.current = current(draft);
                }))
            } else {
                setUsers(produce(draft => {
                    const currentUsers = current(draft) || [];
                    const existingUserIndex = currentUsers.findIndex(item => item.userID === user?.userName);
                    if (existingUserIndex >= 0) {
                        draft[existingUserIndex].isAudioAvailable = false;
                    }
                    usersRef.current = current(draft);
                }))
                callEngine.closeMicrophone();
            }
            setCallInfo(produce((draft) => {
                draft.microphoneOff = !draft.microphoneOff;
            }));

        } else {
            console.error("Call engine is not initialized");
        }
    }, [callInfo, callEngine, user]);

    const onToggleCamera = useCallback(() => {
        if (callEngine) {
            console.log("Switching camera...");

            // On off the camera
            if (!callInfo.cameraOff) {
                callEngine.closeCamera();
                // set users to ensure camera is off
                setUsers(produce(draft => {
                    const existingUser = draft.find(item => item.userID === user?.userName);
                    if (existingUser) {
                        existingUser.isVideoAvailable = false; // Ensure the user has no video available
                    }
                    usersRef.current = current(draft);
                }));

                setCallInfo(produce((draft) => {
                    draft.cameraOff = true;
                }));
            } else {
                // Upgrade call type to video call if audio call and isHost
                if (callInfo.callType === TUICallType.AUDIO_CALL && callInfo.isHost) {
                    setCallInfo(produce((draft) => {
                        draft.callType = TUICallType.VIDEO_CALL;
                    }))
                }
                // Set the me camera ID for the call
                setUsers(produce(draft => {
                    const existingUser = draft.find(item => item.userID === user?.userName);
                    if (existingUser) {
                        existingUser.isVideoAvailable = true; // Ensure the user has video available
                    }
                    usersRef.current = current(draft);
                }));
                setCallInfo(produce((draft) => {
                    draft.cameraOff = false;
                }));
            }

        } else {
            console.error("Call engine is not initialized");
        }
    }, [callInfo, callEngine, user]);

    const mainUser = users.find(user => user.main);
    return (
        <Container className={`call-${props.mode}`} style={{
            display: callInfo.calling && props?.view === 'call' ? 'flex' : 'none',
        }}>


            <CallContainer>
                {
                    (callInfo?.callType === TUICallType.VIDEO_CALL && mainUser?.isVideoAvailable) ? (
                        <FullUserVideo user={mainUser} id={mainCameraIdRef.current}/>
                    ) : (
                        <FullUserProfile user={mainUser}/>
                    )
                }
            </CallContainer>

            <UserCards className={'no-scrollbar'}>
                {
                    users.filter(item => !item?.main).map((user, index) => (
                        <UserCard onClick={() => {
                            // Set the main user when clicked
                            setUsers(produce(draft => {
                                draft.forEach(item => {
                                    item.main = false; // Reset all users to not main
                                });
                                const currentUserIndex = draft.findIndex(item => item.userID === user.userID);
                                if (currentUserIndex !== -1) {
                                    draft[currentUserIndex].main = true; // Set the clicked user as main
                                }
                                usersRef.current = current(draft);
                            }));
                        }} key={index} className={`${user.main ? 'main' : ''} card-style-${index}`} user={user}/>
                    ))
                }
                {
                    props?.mode === 'admin' ? (
                        <InviteButton className={'card-style-invite'} onClick={props?.onCreateInvite}/>
                    ) : null
                }
            </UserCards>


            <Actions className={`actions-${props.mode}`}>
                <MuteAction
                    available={!!callInfo.microphoneList?.length}
                    active={callInfo.microphoneOff}
                    label={T("Mute")}
                    labelOff={T("Unmute")}
                    onClick={onToggleMicrophone}/>
                {
                    props.mode === 'admin' ? null : (
                        <FlipAction
                            available={callInfo?.cameraList?.length > 1 && callInfo?.callType === TUICallType.VIDEO_CALL}
                            active={true}
                            label={T("Flip")}
                            labelOff={T("Flip")}
                            onClick={onChangeCamera}/>
                    )
                }
                <HangupAction
                    available={true}
                    active={true}
                    onClick={onEndCall}
                    end
                    label={T("End")}/>
                <CameraAction
                    available={(callInfo?.callType === TUICallType.VIDEO_CALL || callInfo.isHost) && callInfo?.cameraList?.length > 0}
                    active={!callInfo.cameraOff}
                    label={T("Camera")}
                    labelOff={T("Camera Off")}
                    onClick={onToggleCamera}
                />
            </Actions>
        </Container>
    );
});

export default Call;

const Container = styled.div`
    position: absolute;
    top: 60px;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background-color: #000000;
    align-items: center;
    justify-content: center;

    &.call-customer {
        height: calc(100% - 60px);
        position: fixed;
    }
`;

const CallContainer = styled.div`
    background: radial-gradient(136.46% 91.89% at 50% 52.64%, rgba(12, 12, 12, 0.00) 0%, rgba(0, 0, 0, 0.30) 100%), ${(props: any) => (props.theme.main === "#1D896D" ? '#80DDC8' : props.theme.main)};
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const VideoCallContainer = styled.div``;
